import { Alert, Card, Space, Tag, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    deriveMiddlewareCardState,
    parseEidMiddleware,
    MIDDLEWARE_OFFICIAL_URL,
    type MiddlewareCardState,
} from '../domain/eidMiddleware';
import { probeHardwareService, type HardwareServiceStatus } from '../domain/labelPrinting';

/**
 * What this workstation can say about the Romanian eID middleware.
 *
 * Deliberately a separate card from the hardware-service one above it, because they are two
 * different products with two different owners:
 *
 * - **RexoHardwareService** is maintained and distributed by this project, and this page offers it
 *   for download;
 * - **IDPlugManager** is the national eID middleware, distributed by the Romanian Ministry of
 *   Internal Affairs. This application never bundles, mirrors, proxies, downloads, installs or
 *   starts it — it links to the ministry's page and reports what the workstation observes.
 *
 * Merging them into one card would blur exactly the distinction an operator needs when something
 * is missing: whether to ask us, or to go to the official page.
 */
export interface EidMiddlewareCardProps {
    /** The probe, injectable so tests can drive every state without a workstation. */
    probe?: typeof probeHardwareService;
}

/** How often the card re-reads the workstation, matching the launcher's own reconcile. */
const RECONCILE_INTERVAL_MS = 20_000;

/** The colour each state carries. `unknown` is a warning, never an error: it has not failed. */
const TONE: Record<
    MiddlewareCardState,
    { colour: string; alert: 'success' | 'warning' | 'error' | 'info' }
> = {
    checking: { colour: 'default', alert: 'info' },
    serviceUnreachable: { colour: 'default', alert: 'info' },
    notPaired: { colour: 'default', alert: 'info' },
    unsupported: { colour: 'default', alert: 'info' },
    ready: { colour: 'green', alert: 'success' },
    notInstalled: { colour: 'red', alert: 'error' },
    notRunning: { colour: 'orange', alert: 'warning' },
    untrusted: { colour: 'red', alert: 'error' },
    sessionUnusable: { colour: 'orange', alert: 'warning' },
    unknown: { colour: 'orange', alert: 'warning' },
};

/*
 * The official page is shown in EVERY state, deliberately.
 *
 * It used to be shown only where the card judged the operator "needs" it - not installed, not
 * verifiable, unsupported, unknown - and `notRunning` was not on that list. On a real workstation
 * with the middleware merely stopped, the page therefore rendered zero links to the ministry, which
 * is the one moment somebody is most likely to want the page they got it from. The judgement was
 * the bug: whether a link is useful is the reader's call, not this component's, and a page that
 * names a third-party prerequisite should always say where that prerequisite comes from.
 *
 * The old tests did not catch it because they asserted the four states the list happened to
 * contain. A test that mirrors the implementation cannot contradict it, so they now assert the
 * requirement - one visible official link in every state this card can render.
 */

const browserFetch = (input: string, init?: RequestInit) => window.fetch(input, init);

export const EidMiddlewareCard = ({ probe = probeHardwareService }: EidMiddlewareCardProps) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState<HardwareServiceStatus | null>(null);

    const reload = useCallback(async () => {
        const probed = await probe(browserFetch);

        setStatus(probed);
    }, [probe]);

    useEffect(() => {
        void reload();

        // The same bounded reconcile the launcher uses: an operator who starts the middleware sees
        // this card catch up without reaching for refresh.
        const timer = window.setInterval(() => void reload(), RECONCILE_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [reload]);

    const readiness =
        status?.state === 'ready' ? parseEidMiddleware(status.health.eidMiddleware) : null;
    const state = deriveMiddlewareCardState(status, readiness);
    const tone = TONE[state];

    return (
        <Card
            title={t('hardwareService.middleware.title')}
            style={{ marginBottom: 16 }}
            data-testid="eid-middleware-card"
            extra={
                <Tag color={tone.colour} data-testid="eid-middleware-status">
                    {t(`hardwareService.middleware.status.${state}`)}
                </Tag>
            }
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Says whose product this is, every time. The card sits directly beneath one that
                    offers a download, and an operator must never conclude that this is ours too. */}
                <Typography.Paragraph style={{ marginBottom: 0 }}>
                    {t('hardwareService.middleware.intro')}
                </Typography.Paragraph>

                <Alert
                    type={tone.alert}
                    showIcon
                    message={t(`hardwareService.middleware.status.${state}`)}
                    description={t(`hardwareService.middleware.action.${state}`)}
                    data-testid="eid-middleware-explanation"
                />

                <Space direction="vertical" size="small">
                    <Typography.Text type="secondary">
                        {t('hardwareService.middleware.officialNotice')}
                    </Typography.Text>
                    {/* An ordinary anchor to the ministry's landing page, followed by the browser
                        only when somebody clicks it. Never a direct installer URL, never fetched by
                        this application, and never proxied through our backend - the page publishes
                        its own installers and updates them on its own schedule. */}
                    <Typography.Link
                        href={MIDDLEWARE_OFFICIAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="eid-middleware-official-link"
                    >
                        {t('hardwareService.middleware.officialLink')}
                    </Typography.Link>
                </Space>
            </Space>
        </Card>
    );
};

export default EidMiddlewareCard;
